import PropTypes from "prop-types";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import RtrImage from "../shared/rtr-image";
import ReviewStars from "../shared/review-stars";
import FormattingUtils from "../../../helpers/FormattingUtils";
import Spacer from "images/spacer.svg";
import FitFeedbackTags from "./fit-feedback-tags";
import ModalWithNavigation from "components/design-system/modalWithNavigation";
import { fetchReviewsThumbnailPhotos } from "slices/reviews-thumbnail-carousel-slice";
import { THUMBNAIL_PAGE_SIZE } from "components/source/universal_pdp/review-thumbnail/review-thumbnail-carousel";

export default function ReviewModal({ ind = 0, isOpen, setIsOpen, productId, productDisplayName, reviewId }) {
  const dispatch = useDispatch();
  const reviewsThumbnailPhotos = useSelector(state => state.reviewsThumbnailPhotos);
  const momentsWithPhotos = useSelector(state => state.reviews?.reviewsWithPhotos);
  const reviewsWithPhotos = reviewId ? momentsWithPhotos : reviewsThumbnailPhotos?.moments || [];
  const browser = useSelector(state => state.browser);
  const isMobileViewport = browser?.isMobileViewport;
  const [index, setIndex] = useState(reviewId ? reviewsWithPhotos.findIndex(r => r?.reviewId === reviewId) : ind);

  const totalCount = reviewId ? momentsWithPhotos?.length : reviewsThumbnailPhotos?.totalCount || 0;
  const currentReview = useMemo(() => reviewsWithPhotos[index] || {}, [reviewsWithPhotos, index]);
  // Helper to get the review image
  const reviewImage = review => {
    if (review?.coverPhoto?.urls?.image) {
      return review?.coverPhoto?.urls?.image;
    } else if (Array.isArray(review?.photos) && Number.isInteger(review?.coverPhotoId)) {
      return review.photos?.find(r => r?.photoId === review.coverPhotoId)?.urls?.image ?? "";
    } else {
      return "";
    }
  };

  const fetchMoreReviews = () => {
    if (!reviewsWithPhotos?.length) return;
    if (reviewId) return;
    if (
      index === reviewsWithPhotos.length - 1 &&
      reviewsWithPhotos.length < totalCount &&
      !reviewsThumbnailPhotos?.loading
    ) {
      dispatch(
        fetchReviewsThumbnailPhotos({
          productId: productId,
          offset: reviewsWithPhotos.length,
          limit: THUMBNAIL_PAGE_SIZE,
          orderBy: "newest",
        })
      );
    }
  };

  useEffect(() => {
    fetchMoreReviews();
  }, [index]); // Trigger fetchMoreReviews whenever index changes

  const renderImage = review => (
    <div className="overflow-hidden flex justify-center items-center shrink-0 aspect-[3/4] sm:aspect-auto sm:col-span-3  bg-onyx">
      {reviewImage(review) ? (
        <RtrImage
          className="w-full h-full object-contain"
          alt="Review thumbnail image"
          itemProp="image"
          src={reviewImage(review)}
          fetchpriority="high"
          sizes="(max-width: 480px) 80vw, (max-width: 1024px) 50vw, 1080px"
        />
      ) : (
        <p className="text-center text-gray-500">No image available</p>
      )}
    </div>
  );

  const renderContent = review => {
    const {
      caption,
      rating,
      uploadedAt,
      content,
      sizeWorn,
      firstName,
      isStaff,
      reviewerSnapshot: {
        usStandardSize = "",
        bodyType = "",
        heightInches = "",
        weightPounds = "",
        bustSize = "",
      } = {},
      numReviewsByUser,
    } = review || {};

    const url = "/user_reviews?reviewId=" + review?.reviewId + "&displayName=" + productDisplayName;

    return (
      <div className="sm:col-span-5 bg-white px-4 py-6 sm:p-8 flex flex-col justify-between">
        <div>
          <p className="text-h4 mb-4">{caption}</p>
          <div className="flex gap-4 mb-8">
            {rating && <ReviewStars imageName={`BlackStars${Math.floor(rating)}`} />}
            <p className="text-darkGray">{FormattingUtils.longDate(uploadedAt)}</p>
          </div>
          <p className="mb-8">{content}</p>
          <FitFeedbackTags review={review} />
          <div>
            <div className="mb-6 flex gap-8">
              <p>
                <span className="text-darkGray">Size Worn: </span>
                <span className="text-lg">{sizeWorn}</span>
              </p>
              {usStandardSize && (
                <p>
                  <span className="text-darkGray">Usual Size: </span>
                  <span className="text-lg">{usStandardSize}</span>
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="border-t border-lighterGray pt-4">
          <p className="flex items-center gap-2">
            <span className="text-h4">{firstName ? firstName : "RTR Member"}</span>
            <span className="uppercase text-darkGray">{isStaff ? "Team RTR" : "Top Contributor"}</span>
          </p>
          <div className="mb-6 pt-2 flex items-center gap-2">
            {[
              usStandardSize && `Size ${usStandardSize}`,
              heightInches && FormattingUtils.formatHeight(heightInches),
              weightPounds && `${weightPounds} lbs`,
              bodyType,
              bustSize,
            ]
              .filter(Boolean) // Remove any falsy values (e.g., null, undefined, false)
              .map((content, i, arr) => (
                <Fragment key={i}>
                  <p>{content}</p>
                  {i < arr.length - 1 && <Spacer />} {/* Add Spacer only if it's not the last element */}
                </Fragment>
              ))}
          </div>
          <a href={url} className="underline font-semibold">
            Browse {firstName ? firstName : "RTR Member"}&apos;s Reviews ({numReviewsByUser})
          </a>
        </div>
      </div>
    );
  };

  const renderChildren = () => {
    if (isMobileViewport) {
      return reviewsWithPhotos.map((review, i) => (
        <div key={i} className="w-screen flex-shrink-0 snap-start flex flex-col">
          {renderImage(review)}
          {renderContent(review)}
        </div>
      ));
    }
    return (
      <>
        {renderImage(currentReview)}
        {renderContent(currentReview)}
      </>
    );
  };

  return (
    <ModalWithNavigation
      index={index}
      length={reviewsWithPhotos?.length}
      setIndex={setIndex}
      className="bg-offWhite gap-0.5 sm:bg-white sm:gap-0 sm:grid sm:grid-cols-8"
      modalBackgroundClassName="z-max"
      modalContainerClassName="sm:w-10/12"
      isOpen={isOpen}
      dataTestId="review-images-modal"
      onClose={() => {
        setIsOpen(false);
      }}>
      {renderChildren()}
    </ModalWithNavigation>
  );
}

ReviewModal.propTypes = {
  ind: PropTypes.number,
  productId: PropTypes.string,
  productDisplayName: PropTypes.string,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  reviewId: PropTypes.number,
};
